import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
    CompanyDeactivationManagementControllerService,
    CompanyDeactivationDTO as ApiCompanyDeactivationDTO,
    ScheduleCompanyDeactivationRequest,
    UpdateCompanyDeactivationDateRequest,
    CancelCompanyDeactivationRequest,
    RevertExecutedCompanyDeactivationRequest
} from '@iota/inh-core-v1-management-client';
import {
    CreateDeactivation,
    CreateDeactivationSuccess,
    CreateDeactivationFailure,
    UpdateDeactivation,
    UpdateDeactivationSuccess,
    UpdateDeactivationFailure,
    CancelDeactivation,
    CancelDeactivationSuccess,
    CancelDeactivationFailure,
    RevertDeactivation,
    RevertDeactivationSuccess,
    RevertDeactivationFailure
} from '../actions/company-deactivation.actions';

@Injectable()
export class CompanyDeactivationEffects {
    constructor(
        private actions$: Actions,
        private companyDeactivationService: CompanyDeactivationManagementControllerService
    ) {}

    createDeactivation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CreateDeactivation),
            mergeMap((action) => {
                const request: ScheduleCompanyDeactivationRequest = {
                    companyId: action.companyId,
                    companyDeactivationDate: action.deactivationDate
                };

                return this.companyDeactivationService.scheduleCompanyDeactivation(request).pipe(
                    map((deactivation: ApiCompanyDeactivationDTO) => CreateDeactivationSuccess({ deactivation })),
                    catchError((error) => of(CreateDeactivationFailure({ error: error.message || 'Failed to create deactivation' })))
                );
            })
        )
    );

    updateDeactivation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(UpdateDeactivation),
            mergeMap((action) => {
                const request: UpdateCompanyDeactivationDateRequest = {
                    companyId: action.companyId,
                    newCompanyDeactivationDate: action.deactivationDate
                };

                return this.companyDeactivationService.updateCompanyDeactivationDate(request).pipe(
                    map((deactivation: ApiCompanyDeactivationDTO) => UpdateDeactivationSuccess({ deactivation })),
                    catchError((error) => of(UpdateDeactivationFailure({ error: error.message || 'Failed to update deactivation' })))
                );
            })
        )
    );

    cancelDeactivation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CancelDeactivation),
            mergeMap((action) => {
                const request: CancelCompanyDeactivationRequest = {
                    companyId: action.companyId
                };

                return this.companyDeactivationService.cancelCompanyDeactivation(request).pipe(
                    map(() => CancelDeactivationSuccess({ companyId: action.companyId })),
                    catchError((error) => of(CancelDeactivationFailure({ error: error.message || 'Failed to cancel deactivation' })))
                );
            })
        )
    );

    revertDeactivation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RevertDeactivation),
            mergeMap((action) => {
                const request: RevertExecutedCompanyDeactivationRequest = {
                    companyId: action.companyId
                };

                return this.companyDeactivationService.revertExecutedCompanyDeactivation(request).pipe(
                    map(() => RevertDeactivationSuccess({ companyId: action.companyId })),
                    catchError((error) => of(RevertDeactivationFailure({ error: error.message || 'Failed to revert deactivation' })))
                );
            })
        )
    );
}
